import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  ChevronRightIcon,
  FileText,
  ClipboardList,
  FileSpreadsheet,
  Download,
  Loader2,
} from "lucide-react";
import clsx from "clsx";
import { AllPositionsTable } from "./AllPositionsTable";
import { PagePositionsView } from "./PagePositionsView";
import { usePdfRenderer } from "hooks/usePdfRenderer";
import { pdfFileUrl } from "../utils/fetchResearchReport";
import type {
  BoqExtractionResult,
  Manifest,
  BoqPageResult,
  BoqPosition,
  BoqPage,
} from "./types";
import { exportBoqPositions } from "./exportBoqPositions";

type BoqViewMode = "page" | "all";

export const BoqOverviewTab = ({
  projectId,
  boqExtraction,
}: {
  projectId: string;
  boqExtraction: BoqExtractionResult | null;
  manifest: Manifest | null;
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [viewMode, setViewMode] = useState<BoqViewMode>("page");
  const [pdfLoading, setPdfLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const { renderPage } = usePdfRenderer();

  const pages: BoqPage[] = useMemo(() => {
    const extractionPages = boqExtraction?.pages;
    if (extractionPages && extractionPages.length > 0) {
      return extractionPages.map(
        (p: BoqPageResult): BoqPage => ({
          file: p.file,
          page: p.page,
          sectionContext: p.section_context,
          positionCount: p.positions?.length ?? 0,
          hasError: !!p.error,
        }),
      );
    }
    return [];
  }, [boqExtraction]);

  const currentPage = pages[pageIdx] ?? null;

  const currentPositions: BoqPosition[] = useMemo(() => {
    if (!currentPage || !boqExtraction) return [];
    const extractionPages = boqExtraction.pages;
    if (extractionPages) {
      const pageData = extractionPages.find(
        (p) => p.file === currentPage.file && p.page === currentPage.page,
      );
      if (pageData) return pageData.positions || [];
    }
    return (boqExtraction.positions || []).filter(
      (p) => p.file === currentPage.file && p.page === currentPage.page,
    );
  }, [boqExtraction, currentPage]);

  const totalPositions =
    boqExtraction?.total_positions ?? boqExtraction?.positions?.length ?? 0;
  const pagesWithData =
    boqExtraction?.pages?.filter((p) => (p.positions?.length ?? 0) > 0)
      .length ?? 0;

  const navigateToPage = useCallback(
    (file: string, page: number) => {
      const idx = pages.findIndex((p) => p.file === file && p.page === page);
      if (idx >= 0) {
        setPageIdx(idx);
        setViewMode("page");
      }
    },
    [pages],
  );

  const handleExport = useCallback(async () => {
    if (isExporting || !boqExtraction?.positions?.length) return;
    setIsExporting(true);
    try {
      await exportBoqPositions({
        positions: boqExtraction.positions,
        projectName: boqExtraction.project_name,
        headers: {
          sheetName: t(
            "smartSurveyor.researchReport.exportBoq.sheetName",
            "BoQ Positions",
          ),
          position: "Pos.",
          description: t("smartSurveyor.researchReport.posHeaders.description"),
          quantity: t("smartSurveyor.researchReport.posHeaders.qty"),
          unit: t("smartSurveyor.researchReport.posHeaders.unit"),
          section: t("smartSurveyor.researchReport.posHeaders.section"),
          category: t("smartSurveyor.researchReport.posHeaders.category"),
          specs: t("smartSurveyor.researchReport.exportBoq.specs", "Specs"),
          articleNumber: t(
            "smartSurveyor.researchReport.exportBoq.articleNumber",
            "Article Nr.",
          ),
          manufacturer: t(
            "smartSurveyor.researchReport.exportBoq.manufacturer",
            "Manufacturer",
          ),
        },
      });
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, boqExtraction, t]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !currentPage) return;
    setPdfLoading(true);

    const containerWidth = containerRef.current?.clientWidth || 800;

    const renderPdfPage = async () => {
      const url = await pdfFileUrl(projectId, currentPage.file);
      const result = await renderPage(canvas, url, currentPage.page, {
        containerWidth,
      });
      if (result) setPdfLoading(false);
    };

    renderPdfPage();
  }, [currentPage, projectId, renderPage]);

  return (
    <div className="flex flex-1 overflow-hidden min-h-0">
      {/* Left panel: PDF viewer */}
      <div className="w-1/2 shrink-0 border-r border-gray-200 bg-white flex flex-col">
        {/* Page navigation */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-white shrink-0">
          <button
            type="button"
            onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
            disabled={pageIdx <= 0}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default transition-colors"
          >
            <ChevronLeft size={14} />
          </button>
          <select
            value={pageIdx}
            onChange={(e) => setPageIdx(Number(e.target.value))}
            className="flex-1 text-xs border border-gray-200 rounded-md px-2.5 py-1.5 bg-white truncate focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            {pages.length === 0 && (
              <option value={0}>
                {t("smartSurveyor.researchReport.noBoqPages")}
              </option>
            )}
            {pages.map((p, i) => (
              <option key={i} value={i}>
                p.{p.page} {p.sectionContext || ""} ({p.positionCount} pos.)
                {p.hasError ? " ⚠" : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))}
            disabled={pageIdx >= pages.length - 1}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-default transition-colors"
          >
            <ChevronRightIcon size={14} />
          </button>
          <span className="text-xs text-gray-400 font-mono shrink-0 min-w-[50px] text-center">
            {pages.length > 0 ? `${pageIdx + 1} / ${pages.length}` : "—"}
          </span>
        </div>

        {/* PDF canvas */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-gray-50 relative"
        >
          {currentPage ? (
            <>
              {pdfLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-50 z-10">
                  <div className="relative">
                    <div className="w-16 h-20 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center">
                      <FileText size={28} className="text-gray-300" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-sm">
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 font-medium">
                    {t("smartSurveyor.researchReport.loadingPage")}
                  </span>
                </div>
              )}
              <div className="flex items-start justify-center p-4">
                <canvas ref={canvasRef} className="shadow-lg rounded-sm" />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
              <FileText size={32} className="text-gray-300" />
              <span className="text-sm">
                {t("smartSurveyor.researchReport.noBoqPages")}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Right panel: data */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0 bg-gray-50">
        {/* Tab header */}
        <div className="bg-white border-b border-gray-100 px-5 py-3 shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("page")}
                className={clsx(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                  viewMode === "page"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700",
                )}
              >
                <ClipboardList size={13} />
                {t("smartSurveyor.researchReport.boqTabs.thisPage")}
                {currentPositions.length > 0 && (
                  <span
                    className={clsx(
                      "text-[10px] font-mono px-1.5 py-0.5 rounded-full",
                      viewMode === "page"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-200 text-gray-500",
                    )}
                  >
                    {currentPositions.length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setViewMode("all")}
                className={clsx(
                  "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all",
                  viewMode === "all"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700",
                )}
              >
                <FileSpreadsheet size={13} />
                {t("smartSurveyor.researchReport.boqTabs.positions")}
                {totalPositions > 0 && (
                  <span
                    className={clsx(
                      "text-[10px] font-mono px-1.5 py-0.5 rounded-full",
                      viewMode === "all"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-200 text-gray-500",
                    )}
                  >
                    {totalPositions}
                  </span>
                )}
              </button>
            </div>
            <div className="ml-auto">
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting || !totalPositions}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-40 disabled:cursor-default"
              >
                {isExporting ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Download size={13} />
                )}
                {t(
                  "smartSurveyor.researchReport.exportBoq.button",
                  "Export Excel",
                )}
              </button>
            </div>
          </div>
          {boqExtraction && (
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span>
                {totalPositions} {t("smartSurveyor.researchReport.positions")}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span>
                {pagesWithData} / {pages.length}{" "}
                {t("smartSurveyor.researchReport.pagesWithData")}
              </span>
            </div>
          )}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-5">
          {viewMode === "page" && (
            <PagePositionsView
              currentPage={currentPage}
              positions={currentPositions}
            />
          )}
          {viewMode === "all" && (
            <AllPositionsTable
              positions={boqExtraction?.positions ?? []}
              currentPage={currentPage}
              onNavigateToPage={navigateToPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};
